import { useEffect, useRef, useState } from 'react';
import { compareIso, dateTextToIso, dateTimeTextToIso, getDate } from '../../dateUtils';
import { ITableEditContextState } from './editing/tableEditContext';
import postal from 'postal';
import { focusNextTabable, focusTabableByIndex, getCurrentTabableIndex, getTabableElementByIndex, selectInput } from '../../util/inputUtils';

const down = 40;
const up = 38;
const left = 37;
const right = 39;
const deleteKey = 46;
const tabKey = 9;
const enterKey = 13;
const F5Key = 116;
const insertKey = 45;

function parseCellLocation(cellId: string) {
  const parts = cellId.split('-');

  let column = -1;
  let row = -1;

  if (parts.length < 2) {
    // Should never happen
    return { column, row };
  }
  column = parseInt(parts[parts.length - 1]);
  row = parseInt(parts[parts.length - 2]);

  return { column, row };
}


function scrollCellIntoView(tableId: string, cellNode: Element) {
  const tableNode = document.getElementById(tableId);
  const header = document.getElementById(`${tableId}-header-row`);
  if (!tableId || !header || !tableNode) {
    return;
  }

  const hrect = header.getBoundingClientRect();
  const crect = cellNode.getBoundingClientRect();

  const { xVisible, yVisible, xDelta, yDelta } = isCellScrolledIntoView(tableNode, crect, hrect);

  if (xVisible && yVisible) {
    return;
  }

  if (tableNode) {
    tableNode.scrollTo({
      top: tableNode.scrollTop + yDelta,
      left: tableNode.scrollLeft + xDelta,
      behavior: 'smooth'
    });
  }
}

function isCellScrolledIntoView(tableNode: Element, crect: DOMRect, hrect: DOMRect) {
  const prect = tableNode.getBoundingClientRect();

  const xVisible = crect.left >= prect.left && ((crect.left + crect.width) <= (prect.left + prect.width));
  const yStart = prect.top + hrect.height;
  const yEnd = yStart + tableNode.clientHeight - hrect.height;
  const yVisible = crect.top >= yStart && (crect.top + crect.height) <= yEnd;

  let xDelta = 0;
  let yDelta = 0;

  if (!xVisible) {
    if (prect.left > crect.left) {
      xDelta = -Math.abs(prect.left - crect.left);
    } else {
      xDelta = (crect.left + crect.width) - (prect.left + prect.width);
    }
  }

  if (!yVisible) {
    if (crect.top < yStart) {
      yDelta = -Math.abs(yStart - crect.top);
    } else {
      yDelta = (crect.top + crect.height) - yEnd;
    }
  }

  return { xVisible, yVisible, xDelta, yDelta };
}

function getChildrenTree(x: HTMLElement, result: Array<HTMLElement>) {
  if (!x) {
    return;
  }

  const children = x.childNodes;
  if (children.length <= 0) {
    return;
  }

  children.forEach(x => {
    result.push(x as HTMLElement);
    getChildrenTree(x as HTMLElement, result);
  });
}

function getCellId(tableId: string, col: number) {
  const headerId = `${tableId}-header-${col}`;
  const header = document.getElementById(headerId);
  return header?.dataset?.columnId;
}

function getCellType(tableId: string, accessor: string) {
  const tableEle = document.getElementById(tableId);
  const cell = tableEle?.querySelector(`div[id*="${accessor}"]`);
  if (!cell) return 'text';

  // Check for check mark icons
  if (cell.classList.contains('e-ifms-checkbox-container') || cell.classList.contains('e-ifms-checkbox')) {
    return 'checkbox';
  }
  // Check for date/dateTime
  else if (cell.classList.contains('ifms-table-date')) {
    return 'date'
  }
  else if (cell.classList.contains('ifms-table-number')) {
    return 'number'
  }
  else return 'text'
}

function getCellText(tableId: string, row: number, col: number) {
  const cellId = `${tableId}-cell-${row}-${col}`;
  const cell = document.getElementById(cellId);
  if (!cell) {
    return '';
  }

  // Check for check mark icons
  if (cell.getElementsByClassName('ifms-check-box-icon-true').length > 0 || cell.getElementsByClassName('e-ifms-checkbox').length > 0) {
    return true;
  }
  if (cell.getElementsByClassName('ifms-check-box-icon-false').length > 0 || cell.getElementsByClassName('e-ifms-checkbox-unchecked').length > 0) {
    return false;
  }

  // Check for date/dateTime
  if (cell.getElementsByClassName('ifms-table-date').length > 0) {
    const parseResult = dateTextToIso(cell.innerText);
    return parseResult.valid ? parseResult.iso : '';
    //return '';
  }
  if (cell.getElementsByClassName('ifms-table-datetime').length > 0) {
    const parseResult = dateTimeTextToIso(cell.innerText);
    return parseResult.valid ? parseResult.iso : '';
    //return '';
  }

  // Check for customized value (for example custom renderers)
  let actualCellValue: string | null = null;
  if (cell.dataset?.cellActualValue) {
    actualCellValue = cell.dataset.cellActualValue;
  }
  if (!actualCellValue) {
    const childNodes: Array<HTMLElement> = [];
    getChildrenTree(cell, childNodes);

    const childWithActualValue = childNodes.find(x => !!x.dataset?.cellActualValue);
    if (childWithActualValue) {
      actualCellValue = childWithActualValue.dataset.cellActualValue ?? '';
    }
  }

  if (actualCellValue) {
    return actualCellValue;
  }

  if (col === 0 && cell.getElementsByTagName('img')[0]?.id) //status
    return cell.getElementsByTagName('img')[0].id === 'blueIcon' ? 'OC' : 'PC'

  return cell.innerText.split('\n')[0].replace('\r', '');
}

export default function useEditableTableNavigation(
  tableId: string,
  tableEditContext: ITableEditContextState,
  onDelete: ((editRowIndex: number) => Promise<void>),
  add: (focusRowIndex?: number) => any,
  tableRef: HTMLDivElement | undefined,
  useVirtual: boolean
) {
  const channel = postal.channel('ifms-filter');
  const tableSearchChannel = postal.channel('ifms-table-search');
  const inputChannel = postal.channel('ifms');
  const tableDataLength = tableEditContext.data.length;
  let presetSubscription: ISubscriptionDefinition<any> | null = null;
  let tableSearchSubscription: ISubscriptionDefinition<any> | null = null;
  const startCell = useRef<any>(null);
  const endCell = useRef<any>(null);
  const [doRemoveCellSelectArea, setDoRemoveCellSelectArea] = useState<boolean>(true);
  const directLookupOpenRef = useRef<boolean>(false);
  const [paymentDetailSearch, setPaymentDetailSearch] = useState<string>('');
  const [paymentDetailSearchType, setPaymentDetailSearchType] = useState<string>('');
  const tableEle = document.getElementById(tableId);
  const waitBothChangedRef = useRef(false);

  let isUserScroll = false;
  const scrollHandler = () => isUserScroll = true;

  useEffect(() => {
    if (tableEditContext.editRowIndex === -1 && tableEditContext.editColumnIndex === -1 && !waitBothChangedRef.current) {
      const selectedCellClass = 'data-cell-selected';
      const currentCellSelections = document.getElementById(`${tableId}-body`)?.querySelectorAll(`.${selectedCellClass}`);
      currentCellSelections?.forEach((cell) => {
        cell.classList.remove(selectedCellClass);
      });
    }
    else {
      if (tableEditContext.editRowIndex !== -1 && tableEditContext.editColumnIndex !== -1 && !waitBothChangedRef.current) {
        checkSelectedRowDisplayed(() => { doSelectTableCell(tableEditContext.editRowIndex, tableEditContext.editColumnIndex) });
      }
    }

    if (doRemoveCellSelectArea) {
      removeCellSelectArea();
      setDoRemoveCellSelectArea(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableEditContext.editRowIndex, tableEditContext.editColumnIndex, doRemoveCellSelectArea]);

  useEffect(() => {
    initialize();

    return uninitialize;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRef,
    tableEle,
    tableDataLength,
    tableEditContext.tableColumnLength,
    tableEditContext.editColumnIndex,
    tableEditContext.editRowIndex,
    tableEditContext.tempEditColumnIndex,
    tableEditContext.tableParentDivId,
    tableEditContext.enabled,
    tableEditContext.canAdd,
    tableEditContext.canDeleteRow,
    tableEditContext.initializeFocusedCell]
  );

  useEffect(() => {
    if (!tableRef) return;
    if (tableEditContext.doSelectCell) {
      const editCell = tableRef.querySelector('.data-cell-selected');
      if (editCell) {
        const inputElement = editCell.querySelector('input');
        if (inputElement) {
          setSelectedRowScrollTop(tableEditContext.editRowIndex);
          selectInput(inputElement, true);
          if (inputElement.classList.contains('ifms-lookup') && directLookupOpenRef.current) {
            inputChannel.publish('ifms.lookup-open', {
              targetId: inputElement.id
            });
          }
          else if (inputElement.classList.contains('ifms-select')) {
            inputChannel.publish('ifms.select-open', {
              targetId: inputElement.id
            });
          }
          else if (inputElement.classList.contains('ifms-checkbox')) {
            inputChannel.publish('ifms.checkbox-check', {
              targetId: inputElement.id
            });
          }
        }
      }
    }
  }, [tableRef, tableEditContext.doSelectCell, directLookupOpenRef.current])

  useEffect(() => {
    let searchCol = -1;
    if (paymentDetailSearch !== '' && paymentDetailSearch !== null) {
      if (!tableRef) return;

      var colName = tableEditContext.rowRenderers[tableEditContext.editColumnIndex].accessor;

      if (paymentDetailSearchType === 'date') {
        searchCol = tableEditContext.data.findIndex((x, i) => i > tableEditContext.editRowIndex &&
          (compareIso((getDate(x[colName]) ?? ''), paymentDetailSearch) === 0));
        if (searchCol === -1)
          searchCol = tableEditContext.data.findIndex((x) => compareIso((getDate(x[colName]) ?? ''), paymentDetailSearch) === 0);
      }
      else if (paymentDetailSearchType === 'number') {
        searchCol = tableEditContext.data.findIndex((x, i) => i > tableEditContext.editRowIndex && (parseFloat((x[colName] ?? 0)) === parseFloat(paymentDetailSearch)));
        if (searchCol === -1)
          searchCol = tableEditContext.data.findIndex((x) => (parseFloat((x[colName] ?? 0)) === parseFloat(paymentDetailSearch)));
      }
      else {
        searchCol = tableEditContext.data.findIndex((x, i) => i > tableEditContext.editRowIndex &&
          ((x[colName] ?? '').toString().toLowerCase().includes(paymentDetailSearch.toLowerCase())));
        if (searchCol === -1)
          searchCol = tableEditContext.data.findIndex((x) => (x[colName] ?? '').toString().toLowerCase().includes(paymentDetailSearch.toLowerCase()));
      }

    }
    if (searchCol !== -1) {
      bothChange(searchCol, tableEditContext.editColumnIndex);
      checkSelectedRowDisplayed(() => { doSelectTableCell(searchCol, tableEditContext.editColumnIndex) }, true, searchCol);
    }
    setPaymentDetailSearch('');
  }, [paymentDetailSearch]);

  function removeCellSelectArea() {
    const selectedCellClass = 'data-cell-selected-inner'
    const selectCellArr = document.getElementById(`${tableId}-body`)?.querySelectorAll(`.${selectedCellClass}`);
    selectCellArr?.forEach((cell) => {
      cell.classList.remove(selectedCellClass);
    });
    startCell.current = null;
    endCell.current = null;
  }

  function checkCellSelectArea() {
    if (!tableRef) return;

    const selectCellArr = tableRef.querySelectorAll('.data-cell-selected-inner');
    if (selectCellArr && selectCellArr.length > 0) {
      const cellRange = selectCellArr.length - 1;

      console.log(`startCellId: ${selectCellArr[0].id}`);
      console.log(`endCellId: ${selectCellArr[cellRange].id}`);

      const startCellId = selectCellArr[0].id.split('-');
      const endCellId = selectCellArr[cellRange].id.split('-');

      startCell.current = { row: parseInt(startCellId[startCellId.length - 2] ?? 0), column: parseInt(startCellId[startCellId.length - 1] ?? 0) };
      endCell.current = { row: parseInt(endCellId[endCellId.length - 2] ?? 0), column: parseInt(endCellId[endCellId.length - 1] ?? 0) };
      return true;
    } else {
      startCell.current = null
      endCell.current = null
      return false;
    }
  }

  function copyCell() {
    if (checkCellSelectArea()) {
      // area로 선택 된 경우, 각 cell의 값을 text1 \t text2 \t test3 \n ... 과 같은 패턴으로 클립보드에 저장해야 함.
      var textarea = document.createElement('textarea');

      let startCol = startCell.current.column;
      let endCol = endCell.current.column;
      let startRow = startCell.current.row;
      let endRow = endCell.current.row;

      if (startCol > endCol) {
        startCol = endCell.current.column;
        endCol = startCell.current.column;
      }

      if (startRow > endRow) {
        startRow = endCell.current.row;
        endRow = startCell.current.row;
      }

      let fullText = '';

      for (var i = startRow; i <= endRow; i++) {
        for (var j = startCol; j <= endCol; j++) {
          let text = getCellText(tableId, i, j)?.toString() ?? "";
          fullText += text + (j !== endCol ? '\t' : '');
        }
        fullText += '\n';
      }

      textarea.value = fullText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;

    } else {
      const columnId = getCellId(tableId, getSelectedCol());
      if (columnId) {
        const text = getCellText(tableId, getSelectedRow(), getSelectedCol() ?? "")?.toString() ?? '';
        if (window.isSecureContext) {
          navigator.clipboard.writeText(text)
            .then(() => { console.log("Copy Success"); })
            .catch(() => { console.log("Copy Fail"); });
          return true;
        } else {
          textarea = document.createElement('textarea');
          textarea.value = text;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          return true;
        }
      }
    }
    return true;
  }

  function onEnterKey(directLookupOpen: boolean = true, isTabKey: boolean = false) {
    if (!tableRef) return false;
    const activeElement = document.activeElement as HTMLElement;
    directLookupOpenRef.current = directLookupOpen;

    removeCellSelectArea();

    const editCell = tableRef.querySelector('.data-cell-selected');
    if (editCell) {
      const curElement = editCell.querySelector(`.table-cell-disabled`);
      if (!curElement) {
        tableEditContext.setTempEditColumnIndex(tableEditContext.editColumnIndex);
      }
      else {
        tableEle?.focus();
      }
    }
    else {
      if (isLookupCell(tableEditContext.editRowIndex, tableEditContext.editColumnIndex) && !isTabKey)
        return false;
      else {
        if (tableRef.contains(activeElement)) activeElement.blur();
        tableEditContext.setTempEditColumnIndex(-1);
        doSelectTableCell(tableEditContext.editRowIndex, tableEditContext.editColumnIndex);
      }

    }

    return true;
  }

  function bothChange(row: number, col: number) {
    if (tableEditContext.editColumnIndex === col) {
      setSelectedRow(row, false);
    }
    else if (tableEditContext.editRowIndex === row) {
      setSelectedCol(col, false);
    }
    else {
      setSelectedRow(row, true);
      setSelectedCol(col, false);
    }
  }

  function setSelectedCol(c: number, waitBothChanged: boolean = false) {
    waitBothChangedRef.current = waitBothChanged;
    if (c === 0 && hasIndicatorCell()) c = c + 1;
    tableEditContext.setEditColumnIndex(c);
  }

  function setSelectedRow(c: number, waitBothChanged: boolean = false) {
    waitBothChangedRef.current = waitBothChanged;
    tableEditContext.setEditRowIndex(c);
  }

  function getSelectedCol() {
    waitBothChangedRef.current = false;
    return tableEditContext.editColumnIndex;
  }

  function getSelectedRow() {
    return tableEditContext.editRowIndex;
  }

  function subscribeChannels() {
    presetSubscription = postal.subscribe({
      channel: 'ifms-filter',
      topic: 'ifms-filter.filterPreset',
      callback: () => onSearch(),
    });
    tableSearchSubscription = postal.subscribe({
      channel: 'ifms-table-search-value',
      topic: 'ifms-table-search-value.search',
      callback: ({ value, field }) => {
        setPaymentDetailSearch(value);
        setPaymentDetailSearchType(field);
      },
    });
  }

  function unsubscribeChannels() {
    if (presetSubscription) {
      postal.unsubscribe(presetSubscription);
    }
    if (tableSearchSubscription) {
      postal.unsubscribe(tableSearchSubscription);
    }
  }

  function initialize() {
    subscribeChannels();
    tableRef?.addEventListener('keydown', onTableKeyDown);
    tableRef?.addEventListener('click', onTableClick);
    tableRef?.addEventListener('focusout', onFocusOut);
    tableEle?.addEventListener('scroll', onScroll);
    tableEle?.addEventListener('wheel', scrollHandler);
  }

  function uninitialize() {
    unsubscribeChannels();
    tableRef?.removeEventListener('keydown', onTableKeyDown);
    tableRef?.removeEventListener('click', onTableClick);
    tableRef?.removeEventListener('focusout', onFocusOut);
    tableEle?.removeEventListener('scroll', onScroll);
    tableEle?.removeEventListener('wheel', scrollHandler);
  }

  function findCell(element: Element) {
    const cellIdPrefix = `${tableId}-cell-`;

    for (let i = 0; i <= 7; i++) {
      if (!element) {
        break;
      }

      if (element.id && element.id.startsWith(cellIdPrefix)) {
        return element;
      }

      element = element.parentNode as Element;
    }

    return null;
  }

  function onScroll() {
    if (!tableRef || !useVirtual || !isUserScroll) return;

    const activeElement = document.activeElement as HTMLElement;
    const editCell = tableRef.getElementsByClassName('data-cell-selected');
    const rowId = `${tableId}-row-${getSelectedRow()}`;
    const row = tableRef.querySelector(`#${rowId}`);

    if (row && editCell.length === 0) {
      onEnterKey();
      if (tableRef.contains(activeElement)) tableEle?.focus();
      doSelectTableCell(tableEditContext.editRowIndex, tableEditContext.editColumnIndex, false);
    }

    isUserScroll = false;
  }

  function onFocusOut(e: FocusEvent) {
    setTimeout(() => {
      const relatedTarget = e.relatedTarget as Element;
      const reactModal = document.querySelector('[class^="ReactModal__Content"]');

      if (tableRef && relatedTarget && !tableRef.contains(relatedTarget)) {
        if ((reactModal && !reactModal.contains(tableRef)) || document.querySelector('[id*=lookuptable]') || document.querySelector('[class*=ifms-select-box]')) return;

        removeCellSelectArea();
        clearTableSelection();
        tableEditContext.setTempEditColumnIndex(-1);
      }
    }, 10);
  }

  function onTableClick(e: MouseEvent) {
    if (!tableRef || !tableRef.contains(document.activeElement)) return;

    const target = e.target as Element;

    if (Array.from(target?.classList).some(x => x.startsWith('ifms-dialog-button'))) return;

    const reactModal = document.querySelector('[class^="ReactModal__Content"]');
    if ((reactModal && !reactModal.contains(tableRef)) || document.querySelector('[id*=lookuptable]') || document.querySelector('[class*=ifms-select-box]')) return;

    const { row, column } = parseCellLocation(findCell(target)?.id ?? '');

    if ((row === -1 || column === -1)) {
      const selectCellArr = tableRef.querySelectorAll('.data-cell-selected-inner');
      const header = document.getElementById(`${tableId}-header-row`);
      if (selectCellArr && selectCellArr.length === 0 && tableRef.contains(target) && !header?.contains(target)) {
        const newRowIndex = tableEditContext.addRowOnArrowDownInSpecialCase
          ? tableEditContext.addRowOnArrowDownInSpecialCase()
          : tableDataLength;
        add(newRowIndex);
        //tableEditContext.setTempEditColumnIndex(getFirsFocusableColumnIndex(getSelectedRow() + 1));
      }
      else {
        if (!document.getElementById('table-footer')?.contains(target)) {
          clearTableSelection();
        }
        setDoRemoveCellSelectArea(false);
      }
    }
    else {
      if (tableEditContext.editColumnIndex === column && tableEditContext.editRowIndex === row) {
        directLookupOpenRef.current = true;
        if (!target.closest('.table-cell-disabled') && !target.querySelectorAll('.table-cell-disabled').length)
          tableEditContext.setTempEditColumnIndex(column);
      }
      else {
        tableEditContext.setTempEditColumnIndex(-1);
        bothChange(row, column);
      }
    }
  }

  function handleTableFocusNavigation(currentNode: HTMLElement, elementClassName: string, isForward: boolean, e: Event) {
    if (elementClassName.includes('-hideforfocus-')) {
      const tableId = elementClassName.split('-hideforfocus-')[0];
      const tableElement = document.getElementById(tableId);

      if (tableElement) {
        currentNode?.blur();
        tableElement.focus();

        const tabKeyEvent = new KeyboardEvent('keydown', {
          key: 'Tab',
          code: 'Tab',
          keyCode: 9,
          which: 9,
          shiftKey: !isForward,
          bubbles: true,
          cancelable: true
        });

        tableElement.dispatchEvent(tabKeyEvent);
        e.preventDefault();
      }
    }
  }

  function onTableKeyDown(e: KeyboardEvent) {
    if (!tableRef || !tableRef.contains(document.activeElement)) return;

    const reactModal = document.querySelector('[class^="ReactModal__Content"]');
    if ((reactModal && !reactModal.contains(tableRef)) || document.querySelector('[id*=lookuptable]') || document.querySelector('[class*=ifms-select-box]')) return;

    const currentNode = e.target as HTMLInputElement;
    if (Array.from(currentNode?.classList).some(x => x.startsWith('ifms-dialog-button'))) return;

    const editColIndex = getSelectedCol();
    const editRowIndex = getSelectedRow();
    const tempEditColumnIndex = tableEditContext.tempEditColumnIndex;

    if (e.keyCode === down || e.keyCode === up || e.keyCode === left || e.keyCode === right) {
      const cellId = `${tableId}-cell-${editRowIndex}-${editColIndex}`;
      const cell = tableRef.querySelector(`#${cellId}`);
      const inputElement = cell?.querySelector('input');
      if (tempEditColumnIndex === -1) {
        navigate(e.keyCode, editRowIndex, editColIndex);
      }
      else if (inputElement?.classList.contains('ifms-checkbox')) {
        onEnterKey();
        navigate(e.keyCode, editRowIndex, editColIndex);
      }
      e.stopPropagation();
      e.preventDefault();
    }
    else if ((e.keyCode === deleteKey) || (e.keyCode === 68 && e.ctrlKey)) {
      e.stopPropagation();
      e.preventDefault();

      if (tempEditColumnIndex === -1)
        onDelete(editRowIndex);
    }
    else if (e.keyCode === tabKey) {
      const bottomHideFocusEle = document?.querySelector(`div[class*="${tableId}-hideforfocus-bottom"]`) as HTMLElement;
      const topHideFocusEle = document?.querySelector(`div[class*="${tableId}-hideforfocus-top"]`) as HTMLElement;
      if (e.shiftKey) { //Shift + Tab
        if (tempEditColumnIndex === -1) {
          const isFirstColumn = (editColIndex === 1 && hasIndicatorCell()) || editColIndex === 0;
          if (isFirstColumn) {
            if (editRowIndex === 0) {
              const newIndex = getCurrentTabableIndex(topHideFocusEle) - 1;
              if (newIndex !== -1) {
                const prevElementClassName = getTabableElementByIndex(newIndex).className;
                // Focus할 다음 필드가 Table이라면
                if (prevElementClassName.includes('-hideforfocus-'))
                  handleTableFocusNavigation(currentNode, prevElementClassName, false, e);
                else
                  clearTableSelection();

                focusTabableByIndex(newIndex);
              }
              else {
                bothChange(tableDataLength - 1, tableEditContext.tableColumnLength - 1);
              }
            }
            else {
              bothChange(editRowIndex - 1, tableEditContext.tableColumnLength - 1);
            }
          }
          else {
            if (editColIndex === -1 && editRowIndex === -1) {
              if (tableDataLength === 0) {
                const newIndex = getCurrentTabableIndex(topHideFocusEle) - 1;
                const prevElementClassName = getTabableElementByIndex(newIndex).className;
                // Focus할 다음 필드가 Table이라면
                if (prevElementClassName.includes('-hideforfocus-'))
                  handleTableFocusNavigation(currentNode, prevElementClassName, false, e);
                else
                  focusTabableByIndex(newIndex);
              }
              else {
                bothChange(tableDataLength - 1, tableEditContext.tableColumnLength - 1);
              }
            }
            else {
              setSelectedCol(editColIndex - 1);
            }
          }
        }
        else {
          onEnterKey();
          if (editColIndex !== 0) {
            setSelectedCol(editColIndex - 1);
          }
          else {
            if (editRowIndex !== 0) {
              bothChange(editRowIndex - 1, tableEditContext.tableColumnLength - 1);
            }
            else {
              clearTableSelection();
              const newIndex = getCurrentTabableIndex(topHideFocusEle) - 1;
              focusTabableByIndex(newIndex);
            }
          }
        }
      }
      else {
        if (tempEditColumnIndex === -1) {
          if (editColIndex !== tableEditContext.tableColumnLength - 1) {
            if (editColIndex === -1 && editRowIndex === -1) {
              if (tableDataLength === 0) {
                //다음 필드가 Table이라면
                const nextTabableElementClassName = focusNextTabable(bottomHideFocusEle as HTMLInputElement, true)?.className;
                if (nextTabableElementClassName && nextTabableElementClassName.includes('-hideforfocus-'))
                  handleTableFocusNavigation(currentNode, nextTabableElementClassName, true, e);
                else
                  focusNextTabable(bottomHideFocusEle, false, true);
              }
              else {
                bothChange(0, hasIndicatorCell() ? 1 : 0);
              }
            }
            else {
              setSelectedCol(editColIndex + 1);
            }
          }
          else {
            if (editRowIndex !== tableDataLength - 1) {
              bothChange(editRowIndex + 1, 0);
            }
            else {
              clearTableSelection();

              //다음 필드가 Table이라면
              const nextTabableElementClassName = focusNextTabable(bottomHideFocusEle as HTMLInputElement, true)?.className;
              if (nextTabableElementClassName && nextTabableElementClassName.includes('-hideforfocus-'))
                handleTableFocusNavigation(currentNode, nextTabableElementClassName, true, e);
              else
                focusNextTabable(bottomHideFocusEle, false, true);
            }
          }
        }
        else {
          onEnterKey(true, true);
          if (editColIndex !== tableEditContext.tableColumnLength - 1) {
            setSelectedCol(editColIndex + 1);
          }
          else {
            if (editRowIndex !== tableDataLength - 1) {
              bothChange(editRowIndex + 1, 0);
            }
            else {
              clearTableSelection();
              focusNextTabable(bottomHideFocusEle, false, true);
            }
          }
        }
      }
      e.stopPropagation();
      e.preventDefault();
    }
    else if (e.keyCode === enterKey) {
      if (!isDisplayedRow(tableEditContext.editRowIndex)) {
        e.preventDefault();
        e.stopPropagation();
      }
      checkSelectedRowDisplayed(() => { onEnterKey(isDisplayedRow(tableEditContext.editRowIndex)) });
    }
    else if (e.ctrlKey && e.keyCode === 67) { //copy
      copyCell();
    }
    else if (e.keyCode === F5Key) {
      onSearch();
      e.stopPropagation();
      e.preventDefault();
    }
    else if (e.keyCode === insertKey) {
      checkSelectedRowDisplayed(() => { add(editRowIndex) });
    }
    else {
      if (editColIndex === -1 || editRowIndex === -1) return;
      if (e.key === 'Shift' || (e.keyCode === 83 && e.ctrlKey)) {
        return; // Shift만 혹은 Ctrl+S를 눌렀을 때는 이벤트 처리하지 않고 종료 
      }
      const isBasicLetter = e.keyCode >= 65 && e.keyCode <= 90;  // A-Z
      const isBasicNumber = e.keyCode >= 48 && e.keyCode <= 57;  // 0-9
      const isNumPadNumber = e.keyCode >= 96 && e.keyCode <= 105;  // Numpad 0-9
      const numpadOperators = [
        111,  // Numpad / (나눗셈)
        106,  // Numpad * (곱셈)
        109,  // Numpad - (뺄셈)
        107,  // Numpad + (덧셈)
        110   // Numpad . (소수점)
      ];
      const specialChars = [ // 특수문자 (Shift + 숫자키 등으로 입력되는 문자들)
        192,  // `(백틱)
        189,  // -(하이픈)
        187,  // =(등호)
        219,  // [(왼쪽 대괄호)
        221,  // ](오른쪽 대괄호)
        220,  // \(백슬래시)
        186,  // ;(세미콜론)
        222,  // '(작은따옴표)
        188,  // ,(쉼표)
        190,  // .(마침표)
        191   // /(슬래시)
      ];
      // 허용할 특수키들
      const allowedKeys = [
        //8,  // Backspace
        16,   // Shift
        32,   // Spacebar
      ];

      const isAllowed =
        isBasicLetter ||
        isBasicNumber ||
        isNumPadNumber ||
        numpadOperators.includes(e.keyCode) ||
        specialChars.includes(e.keyCode) ||
        allowedKeys.includes(e.keyCode);

      if (isAllowed && tempEditColumnIndex === -1) {
        const editCell = tableRef.querySelector('.data-cell-selected');
        const inputElement = editCell?.querySelector('input');
        if (editCell && inputElement && inputElement?.classList.contains('ifms-checkbox') && e.keyCode !== 32)
          return;

        if (!isDisplayedRow(tableEditContext.editRowIndex)) {
          e.preventDefault();
          e.stopPropagation();
        }
        checkSelectedRowDisplayed(() => { onEnterKey(e.keyCode === 13); });
      }
    }
  }

  function onSearch() {
    const columnId = getCellId(tableId, getSelectedCol());
    if (columnId) {
      if (tableEditContext.useTableSearch) {
        if (tableEditContext.tempEditColumnIndex !== -1) onEnterKey();
        tableSearchChannel.publish('ifms-table-search.open',
          {
            columnId,
            value: getCellText(tableId, getSelectedRow(), tableEditContext.editColumnIndex),
            //GetcellText를 하면 amount 1000이 1,000으로 넘어와서 Getdecimal을 하면 1로 변환됨
            numValue: tableEditContext.data[tableEditContext.editRowIndex][columnId ?? ''],
            type: getCellType(tableId, columnId),
            cur: tableEditContext.data[tableEditContext.editRowIndex].currency
          });
        return true;
      }

      if (tableEditContext.initializeFocusedCell === undefined) return;

      channel.publish('ifms-filter.preset', { columnId, value: getCellText(tableId, getSelectedRow(), tableEditContext.editColumnIndex) });
      return true;
    }
  }

  function hasIndicatorCell(): boolean {
    return !!tableEle?.querySelector('.ifms-table-indicator')
  }

  function isLookupCell(rowIndex: number, colIndex: number): boolean {
    if (!tableRef) return true;
    const editCellClass = `.ifms-editable-cell-${rowIndex}-${colIndex}`;
    const editCell = tableRef.querySelector<HTMLDivElement>(editCellClass);
    if (!editCell) {
      return false;
    }

    return !!editCell.querySelector('.ifms-lookup');
  }

  function isDisplayedRow(rowNum: number) {
    if (!tableEle) return false;
    const rowsEle = document.getElementById(`${tableId}-body`)?.querySelector('div[role="row"]') as HTMLDivElement;
    const header = document.getElementById(`${tableId}-header-row`);
    const rowHeight = rowsEle.clientHeight;
    const maxDisplayRowNum = Math.floor(((tableEle.clientHeight ?? 0) - (header?.clientHeight ?? 0)) / rowHeight);

    return Math.ceil((tableEle.scrollTop - 3) / rowHeight) <= rowNum && Math.floor(maxDisplayRowNum + ((tableEle.scrollTop + 3) / rowHeight)) >= rowNum
  }

  function setSelectedRowScrollTop(findIndex: number) {
    if (!tableEle) return;
    const selectedRowIndex = findIndex;
    if (isDisplayedRow(selectedRowIndex)) return;
    const rowsEle = document.getElementById(`${tableId}-body`)?.querySelector('div[role="row"]') as HTMLDivElement;

    const rowHeight = rowsEle.clientHeight;
    const header = document.getElementById(`${tableId}-header-row`);
    const maxDisplayRowNum = Math.floor(((tableEle.clientHeight ?? 0) - (header?.clientHeight ?? 0)) / rowHeight);

    let newScrollTop = 0;

    if (selectedRowIndex + maxDisplayRowNum > tableDataLength)
      newScrollTop = (tableDataLength - maxDisplayRowNum) * rowHeight;
    else newScrollTop = selectedRowIndex * rowHeight;

    let scrollToOptions = {
      top: newScrollTop,
    }

    tableEle.scrollTo(scrollToOptions);
  }

  function checkSelectedRowDisplayed(callback: any, forceFind: boolean = false, findRowIndex: number = -1) {
    if (!tableRef) return;

    const waitForSelectedRowDisplayed = (renderCallback: () => void) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(renderCallback);
      });
    };

    const selectedRowIndex = findRowIndex === -1 ? getSelectedRow() : findRowIndex;

    const rowId = `${tableId}-row-${selectedRowIndex}`;
    const row = tableRef.querySelector(`#${rowId}`);
    //const editCell = tableRef.querySelector('.data-cell-selected');

    if (row && !forceFind) {
      callback();
    }
    else {
      setSelectedRowScrollTop(selectedRowIndex);

      waitForSelectedRowDisplayed(() => {
        doSelectTableCell(selectedRowIndex, tableEditContext.editColumnIndex);
        callback();
      });
    }
  }

  function navigate(keyCode: number, rowIndex: number, columnIndex: number) {
    return checkSelectedRowDisplayed(() => {
      if (!tableRef || !tableRef.offsetParent) return;
      const isFirstColumn = (columnIndex === 1 && hasIndicatorCell()) || columnIndex === 0;

      if (keyCode === up) {
        if (rowIndex === 0) {
          return;
        }
        setSelectedRow(rowIndex - 1)
      } else if (keyCode === down) {
        if (tableEditContext.addRowOnArrowDownInSpecialCase) {
          const newRowIndex = tableEditContext.addRowOnArrowDownInSpecialCase(rowIndex);
          if (newRowIndex === -1) {
            if (rowIndex < tableDataLength - 1) {
              setSelectedRow(rowIndex + 1);
            }
          }
          else {
            if (rowIndex < tableDataLength - 1)
              setSelectedRow(rowIndex + 1);
            else
              add(newRowIndex);
          }
        } else if (rowIndex === tableDataLength - 1) {
          add(tableDataLength);
        } else {
          if (rowIndex < tableDataLength - 1) {
            setSelectedRow(rowIndex + 1);
          }
        }
      } else if (keyCode === left) {
        if (isFirstColumn) return;
        setSelectedCol(columnIndex - 1);
      } else if (keyCode === right) {
        if (columnIndex === tableEditContext.tableColumnLength - 1) return;
        setSelectedCol(columnIndex + 1);
      }
    });
  }

  function doSelectTableCell(row: number, col: number, moveScroll: boolean = true) {
    if (row < 0 || !tableRef) {
      return false;
    }
    const selectedCellClass = 'data-cell-selected';
    const cellId = `${tableId}-cell-${row}-${col}`;
    const currentCellSelections = tableRef.querySelectorAll(`.${selectedCellClass}`);

    if (currentCellSelections && currentCellSelections.length > 0) {
      // if (col === -1) {
      //   currentCellSelections[0].classList.remove(selectedCellClass);
      //   return;
      // }

      // if (currentCellSelections[0].id === cellId) {
      //   // Required cell already selected
      //   return false;
      // } else {
      currentCellSelections.forEach((cell) => {
        cell.classList.remove(selectedCellClass);
      });
      //}
    }

    const cell = tableRef.querySelector(`#${cellId}`);
    if (cell) {
      cell.classList.add(selectedCellClass);
      if (moveScroll) scrollCellIntoView(tableId, cell);
      const reactModal = document.querySelector('[class^="ReactModal__Content"]');
      if (reactModal && !reactModal.contains(tableRef)) return true;
      tableEle?.focus();
      return true;
    }

    return false;
  }

  function clearTableSelection() {
    setSelectedRow(-1, true);
    setSelectedCol(-1, false);
  }

  return {
    clearTableSelection,
  };
}
